/**
 * Card 2508 (D2) — Import: bring a firm's existing register in once (the approved prototype's
 * screen 4, "Import your existing clients").
 *
 * <p>Two steps, and nothing is saved by the first. The staffer chooses the register as a CSV file;
 * the page sends it for a CHECK (`dry_run=true`) and shows what an import would do: rows read, rows
 * ready, rows that need a look (with the engine's reasons, downloadable), values the declared
 * mapping could not read, and records already imported (skipped, never updated). Then Import sends
 * the same file for real and says what it did.</p>
 *
 * <p>How the register's columns are read is the workflow's DECLARED `import:` block, on the engine
 * side. Who may import is the engine's too (the declaration's `required_role`): a refusal comes back
 * as a 403 and is said in words. The page is offered in the nav only for a board whose families row
 * says it takes imports.</p>
 *
 * <p>THE FILE IS UNTRUSTED. Its values reach this page only as the engine's answer, rendered as text
 * (React escapes it), and the needs-a-look download neutralises a leading `= + - @` so a spreadsheet
 * opening it never runs a formula.</p>
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, EmptyState, PageHeader, Pill, Table, type Column } from "@ardept/design-system";
import { ApiError } from "../api/fetcher.js";
import {
  IMPORT_MAX_BYTES,
  applyImport,
  checkImport,
  joinOr,
  useClientTenant,
  useFamilies,
  useJobStatusFiles,
} from "../api/portalQueries.js";
import type { ActionErrorBody, ImportApplyResponse, ImportDryRunResponse, ImportNeedsLookRow } from "../api/types.js";
import { boardFor, importLabel } from "../nav/familyRegistry.js";
import { usePageTitle } from "../nav/usePageTitle.js";
import { columnsFor, placeholderText } from "./boardFields.js";
import { countLabel } from "./ViewsBoardPage.js";
import "./FilesBoardPage.css";
import "./FileDetailPage.css";
import "./ViewsBoardPage.css";
import "./ImportPage.css";
import { useVendorCopy } from "../theme/vendorCopy.js";

// ── The download ────────────────────────────────────────────────────────────────────────────

/**
 * One CSV cell, safe to open in a spreadsheet: a value starting with `=`, `+`, `-`, `@` (or a tab or
 * carriage return) gets a leading apostrophe so it is read as text, never run as a formula; a value
 * with a comma, quote or line break is quoted.
 */
export function csvCell(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** The needs-a-look rows as a CSV file's text: row number, key, and the reasons. */
export function needsLookCsv(rows: readonly ImportNeedsLookRow[]): string {
  const lines = [["Row", "Key", "Why it needs a look"].map(csvCell).join(",")];
  for (const row of rows) {
    lines.push([String(row.row), row.key ?? "", row.reasons.join("; ")].map(csvCell).join(","));
  }
  return `${lines.join("\r\n")}\r\n`;
}

/** Hand the browser a file to save. */
function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ── Refusals, in words ──────────────────────────────────────────────────────────────────────

/** Why an import (or its check) was refused, in the words the page shows. */
export function describeImportError(error: unknown): string {
  if (!(error instanceof ApiError)) return "That did not go through. Please try again.";
  let body: ActionErrorBody = {};
  try {
    body = JSON.parse(error.bodyText) as ActionErrorBody;
  } catch {
    body = {};
  }
  const code = body.error_code ?? body.error;
  if (error.status === 403) {
    const holders = (body.holders ?? []).filter((holder) => holder.trim() !== "");
    const who =
      holders.length > 0
        ? joinOr(holders)
        : body.required_role
          ? `people with the ${body.required_role} role`
          : "people with the right role";
    return `Only ${who} can import. Ask one of them to do it.`;
  }
  if (error.status === 413) return "That file is too big for one import. The most it can take is 2 MB.";
  if (error.status === 400 && code === "import_not_declared") return "This board does not take imports.";
  if (error.status === 400 && (code === "too_many_rows" || code === "row_cap_exceeded")) {
    return "That file has more rows than one import can take. Split it and import each part.";
  }
  if (error.status === 400) {
    return "That file could not be read. Check it is the register saved as CSV, with its header row, and try again.";
  }
  return "That did not go through. Please try again.";
}

// ── Pieces ──────────────────────────────────────────────────────────────────────────────────

interface UnmappedRow {
  id: string;
  header: string;
  value: string;
  count: number;
}

/** header → value → count, flattened into rows, most common first within each header. */
export function unmappedRows(unmapped: ImportDryRunResponse["unmapped"]): UnmappedRow[] {
  return Object.entries(unmapped).flatMap(([header, values]) =>
    Object.entries(values)
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => ({ id: `${header}\u0000${value}`, header, value, count })),
  );
}

const UNMAPPED_COLUMNS: Column<UnmappedRow>[] = [
  { key: "header", label: "Column", render: (row) => row.header },
  {
    key: "value",
    label: "Value",
    render: (row) => (row.value.trim() === "" ? <span className="vb-none">blank</span> : <code className="im-value">{row.value}</code>),
  },
  { key: "count", label: "Rows", align: "right", render: (row) => countLabel(row.count) },
];

interface LookRow {
  id: string;
  row: ImportNeedsLookRow;
}

const LOOK_COLUMNS: Column<LookRow>[] = [
  { key: "row", label: "Row", align: "right", render: (r) => r.row.row },
  {
    key: "key",
    label: "Key",
    render: (r) => (r.row.key === null || r.row.key === "" ? <span className="vb-none">none</span> : <code className="im-value">{r.row.key}</code>),
  },
  { key: "why", label: "Why it needs a look", render: (r) => r.row.reasons.join("; ") },
];

/** The other first-date rules, listed so the choice is visible; only the placeholder one is built. */
const LATER_RULES: readonly { title: string; body: string }[] = [
  {
    title: "Spread the first dates over the coming year, highest risk first",
    body: "High-risk records in the first months, then medium, then low. After that each follows its band.",
  },
  {
    title: "Use the date of each record's last check",
    body: "If your practice software or files hold it, each date lands on its real anniversary.",
  },
  {
    title: "Start from ID expiry where there is one",
    body: "Records with an ID expiry date are due when it runs out; the rest are spread over the year.",
  },
];

/** "1 client" / "3 clients": a count and the board's noun, agreeing. */
export function counted(count: number, plural: string): string {
  const singular = plural.endsWith("s") ? plural.slice(0, -1) : plural;
  return `${countLabel(count)} ${count === 1 ? singular : plural}`;
}

/** What an import did, in one sentence after the heading. */
export function importedSentence(done: ImportApplyResponse): string {
  const parts: string[] = [];
  if (done.skipped_existing > 0) {
    parts.push(`${countLabel(done.skipped_existing)} ${done.skipped_existing === 1 ? "was" : "were"} already here and skipped.`);
  }
  if (done.needs_look_count > 0) {
    parts.push(`${countLabel(done.needs_look_count)} ${done.needs_look_count === 1 ? "needs" : "need"} a look and ${done.needs_look_count === 1 ? "was" : "were"} not imported.`);
  }
  // The engine could not start these ready rows (it logs why). Nothing is lost: importing the same
  // file again picks them up, because every row already imported is skipped.
  if (done.failed > 0) {
    parts.push(`${countLabel(done.failed)} could not be imported just now. Import the same file again to retry ${done.failed === 1 ? "it" : "them"}.`);
  }
  return parts.join(" ");
}

// ── The page ────────────────────────────────────────────────────────────────────────────────

export function ImportPage(): JSX.Element {
  const { contact } = useVendorCopy();
  const { workflowId = "" } = useParams<{ workflowId: string }>();
  const identity = useClientTenant();
  const tenantId = identity.tenantId;
  const families = useFamilies(tenantId);
  const board = boardFor(families.data?.families, workflowId);
  const title = board === undefined ? "Import" : importLabel(board);
  usePageTitle(title);
  const noun = board === undefined ? "records" : board.label.toLowerCase();
  const queryClient = useQueryClient();

  // The board's first page (the same read, and cache entry, the board itself makes): whether the
  // board takes an import at all (`has_import`), and whether its dates have a placeholder rule (D5).
  const overview = useJobStatusFiles(tenantId, {
    workflowId,
    enabled: board !== undefined && board.workflowId !== null,
  });
  const placeholder = useMemo(() => {
    const files = overview.data?.files ?? [];
    for (const column of columnsFor(files)) {
      if (column.placeholderFlag === null) continue;
      const flagged = files.find((file) => file.fields?.[column.placeholderFlag!] !== undefined);
      return placeholderText(flagged?.fields, column);
    }
    return null;
  }, [overview.data]);

  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [fileProblem, setFileProblem] = useState<string | null>(null);

  const check = useMutation<ImportDryRunResponse, Error, string>({
    mutationFn: (csv) => checkImport(tenantId ?? "", workflowId, csv),
  });
  const apply = useMutation<ImportApplyResponse, Error, string>({
    mutationFn: (csv) => applyImport(tenantId ?? "", workflowId, csv),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["job-status-files", tenantId] });
    },
  });

  const choose = async (chosen: File | undefined): Promise<void> => {
    check.reset();
    apply.reset();
    setFile(null);
    setFileProblem(null);
    if (chosen === undefined) return;
    if (chosen.size > IMPORT_MAX_BYTES) {
      setFileProblem("That file is too big for one import. The most it can take is 2 MB.");
      return;
    }
    const text = await readText(chosen);
    if (text.trim() === "") {
      setFileProblem("That file is empty. Choose the register saved as CSV.");
      return;
    }
    setFile({ name: chosen.name, text });
    check.mutate(text);
  };

  const result = check.data;
  const done = apply.data;
  // Known once the families say there is no such board, or the board read has answered (or failed).
  const boardReadFailed = board !== undefined && overview.error !== null;
  const familiesKnown =
    families.data !== undefined && (board === undefined || overview.data !== undefined || boardReadFailed);
  const takesImports = board !== undefined && overview.data?.has_import === true;

  return (
    <div className="import-page files-board" data-testid="import-page">
      {board !== undefined && (
        <Link className="fd-back" to={board.route}>
          <span aria-hidden="true">&larr;</span> Back to {board.label}
        </Link>
      )}
      <PageHeader
        title={title}
        subtitle="Bring your existing register in once, so every record is here from day one. Nothing is saved until you press Import."
      />

      {boardReadFailed && (
        <div className="state state--error" role="alert">
          <p className="state__body">We could not check this board just now. Refresh the page to try again.</p>
        </div>
      )}

      {familiesKnown && !boardReadFailed && !takesImports && (
        <div className="state" role="status">
          <h2 className="state__title">There is nothing to import here</h2>
          <p className="state__body">This board does not take an import. Speak to {contact} if you expected one.</p>
        </div>
      )}

      {takesImports && (
        <>
          <section className="im-card" aria-labelledby="im-file-h">
            <div className="im-card__head">
              <h2 id="im-file-h" className="im-card__title">
                1 · The register
              </h2>
              {result !== undefined && <Pill tone="good">Checked</Pill>}
            </div>
            <div className="im-card__body">
              <label className="im-file">
                <span className="lt-label">Choose the register as a CSV file</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  disabled={check.isPending || apply.isPending}
                  onChange={(event) => void choose(event.target.files?.[0])}
                />
              </label>
              <p className="im-hint">
                In Google Sheets, open the register&rsquo;s first sheet, then File, Download, Comma-separated values.
              </p>
              {fileProblem !== null && (
                <p className="lt-error" role="alert">
                  {fileProblem}
                </p>
              )}
              {check.isPending && (
                <p className="im-hint" aria-live="polite">
                  Checking {file?.name}…
                </p>
              )}
              {check.error !== null && (
                <div className="state state--error" role="alert">
                  <p className="state__body">{describeImportError(check.error)}</p>
                </div>
              )}
              {result !== undefined && (
                <div className="im-numbers" aria-label="What the check found">
                  <div className="im-number">
                    <span className="im-number__n num">{countLabel(result.rows_read)}</span>
                    <span className="im-number__l">rows read from {file?.name}</span>
                  </div>
                  <div className="im-number">
                    <span className="im-number__n num">{countLabel(result.ready)}</span>
                    <span className="im-number__l">ready to import as they are</span>
                  </div>
                  <div className="im-number">
                    <span className={`im-number__n num${result.needs_look.length > 0 ? " im-number__n--warn" : ""}`}>
                      {countLabel(result.needs_look.length)}
                    </span>
                    <span className="im-number__l">need a look (below)</span>
                  </div>
                  <div className="im-number">
                    <span className="im-number__n num">{countLabel(result.existing.length)}</span>
                    <span className="im-number__l">already here, skipped</span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {result !== undefined && (
            <>
              <section className="im-card" aria-labelledby="im-unmapped-h">
                <div className="im-card__head">
                  <h2 id="im-unmapped-h" className="im-card__title">
                    2 · Values the import could not read
                  </h2>
                </div>
                <Table<UnmappedRow>
                  columns={UNMAPPED_COLUMNS}
                  rows={unmappedRows(result.unmapped)}
                  caption="Values the import could not read"
                  emptyState={<p className="im-hint">Every value in the register was read.</p>}
                />
              </section>

              <section className="im-card" aria-labelledby="im-look-h">
                <div className="im-card__head">
                  <h2 id="im-look-h" className="im-card__title">
                    Rows that need a look
                  </h2>
                  {result.needs_look.length > 0 && (
                    <Button
                      variant="ghost"
                      onClick={() => download("rows-to-look-at.csv", needsLookCsv(result.needs_look))}
                    >
                      Download the {countLabel(result.needs_look.length)} to look at
                    </Button>
                  )}
                </div>
                <Table<LookRow>
                  columns={LOOK_COLUMNS}
                  rows={result.needs_look.map((row, index) => ({ id: `${row.row}-${index}`, row }))}
                  caption="Rows that need a look"
                  emptyState={<p className="im-hint">Nothing needs a look. Every row is ready.</p>}
                />
              </section>
            </>
          )}

          {placeholder !== null && (
            <section className="im-card" aria-labelledby="im-dates-h">
              <div className="im-card__head">
                <h2 id="im-dates-h" className="im-card__title">
                  3 · First dates
                </h2>
                <Pill tone="attention">Your decision, later</Pill>
              </div>
              <div className="im-card__body">
                <p className="im-p">
                  The register holds no date for each record&rsquo;s last check, so the first dates need a rule. Until
                  it is agreed, every imported record gets the same stand-in date and reads &ldquo;{placeholder}&rdquo;.
                </p>
                <ul className="im-rules">
                  <li className="im-rule is-on">
                    <span className="im-rule__title">Use a stand-in date for now</span>
                    <span className="im-rule__body">What Import does today. A later import or a change of date replaces it.</span>
                    <Pill tone="good">In use</Pill>
                  </li>
                  {LATER_RULES.map((rule) => (
                    <li key={rule.title} className="im-rule">
                      <span className="im-rule__title">{rule.title}</span>
                      <span className="im-rule__body">{rule.body}</span>
                      <Pill tone="muted">Decided with you later</Pill>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          <div className="im-go">
            <Button
              variant="primary"
              size="lg"
              disabled={result === undefined || result.would_start === 0 || apply.isPending || done !== undefined}
              onClick={() => file !== null && apply.mutate(file.text)}
            >
              {apply.isPending
                ? "Importing…"
                : result === undefined
                  ? "Import"
                  : `Import ${counted(result.would_start, noun)}`}
            </Button>
            {result !== undefined && result.would_start === 0 && (
              <span className="im-hint">Nothing new to import from this file.</span>
            )}
          </div>

          {apply.error !== null && (
            <div className="state state--error" role="alert">
              <p className="state__body">{describeImportError(apply.error)}</p>
            </div>
          )}

          {done !== undefined && board !== undefined && (
            <div className="state" role="status">
              <h2 className="state__title">
                Imported {counted(done.started, noun)}
              </h2>
              <p className="state__body">
                {importedSentence(done)} <Link to={board.route}>Go to {board.label}</Link>
              </p>
            </div>
          )}
        </>
      )}

      {!familiesKnown && families.error === null && (
        <EmptyState iconName="file-edit" title="Loading…" body="Checking what this board takes." />
      )}
    </div>
  );
}

/** A chosen file's text. `Blob.text()` where the browser has it, a FileReader otherwise. */
function readText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

export default ImportPage;
